import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, Trash2 } from "lucide-react";
import { api, type Site } from "../lib/api";
import { siteHost } from "../lib/domains";
import { useAppDialog } from "./AppDialog";

export default function DeleteSiteButton({
  site,
  onDeleted,
  className = "btn",
}: {
  site: Pick<Site, "id" | "subdomain" | "archivedAt" | "pendingBuildId">;
  onDeleted?: (siteId: string) => void;
  className?: string;
}) {
  const navigate = useNavigate();
  const dialog = useAppDialog();
  const [deleting, setDeleting] = useState(false);

  async function remove() {
    if (!site.archivedAt || site.pendingBuildId) return;
    const confirmed = await dialog.promptText({
      title: `Delete the entire ${siteHost(site.subdomain)} site?`,
      body: "This permanently removes the entire website, its version history, previews, and uploaded files. Its live address stops working. This cannot be undone.",
      label: `Type ${site.subdomain} to confirm`,
      requiredValue: site.subdomain,
      confirmLabel: "Delete entire site",
      tone: "danger",
    });
    if (confirmed !== site.subdomain) return;
    setDeleting(true);
    try {
      await api.deleteSite(site.id, confirmed);
      onDeleted?.(site.id);
      if (!onDeleted) navigate("/dashboard", { replace: true });
    } catch (reason: any) {
      await dialog.notify({
        title: "The site could not be deleted",
        body: reason?.payload?.error || reason?.message || "Something went wrong on our side. Try again in a moment.",
        tone: "danger",
      });
      setDeleting(false);
    }
  }

  if (!site.archivedAt) return <Link to="/sites" className={className} title="Archive this site in your workspace. Its live website and history stay available.">
    <Archive size={14} aria-hidden="true" /> Archive in workspace
  </Link>;

  return (
    <button type="button" className={`${className} delete-site-button`} onClick={() => void remove()} disabled={deleting || !!site.pendingBuildId}
      title={site.pendingBuildId ? "Wait for or cancel the running build before deleting the entire site." : undefined}>
      <Trash2 size={14} />
      {deleting ? "Deleting…" : "Delete entire site"}
    </button>
  );
}
